import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => {
    return localStorage.getItem('peerstudy-theme') === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('peerstudy-theme', dark ? 'dark' : 'light');
  }, [dark]);

  const toggle = () => setDark(prev => !prev);

  const t = dark ? {
    // Dark theme
    bg: '#0D1117',
    bgGradient: 'linear-gradient(160deg, #0D1117 0%, #161B22 40%, #1C2128 100%)',
    card: 'rgba(22,27,34,0.9)',
    cardBorder: 'rgba(48,54,61,0.6)',
    cardShadow: '0 2px 12px rgba(0,0,0,0.3)',
    nav: 'rgba(13,17,23,0.85)',
    navBorder: 'rgba(48,54,61,0.4)',
    text: '#E6EDF3',
    textSecondary: '#8B949E',
    textMuted: '#484F58',
    input: '#161B22',
    inputBorder: '#30363D',
    button: 'linear-gradient(135deg, #3B7DD8 0%, #2E6BC4 100%)',
    panel: '#161B22',
    panelBorder: '#30363D',
    sectionBg: '#1C2128',
    sectionBorder: 'rgba(48,54,61,0.5)',
    chatBubble: '#1C2128',
    chatBubbleBorder: '#30363D',
    filterActive: '#E6EDF3',
    filterActiveBg: '#30363D',
    filterInactive: '#8B949E',
    filterInactiveBg: 'rgba(22,27,34,0.6)',
    overlay: 'rgba(0,0,0,0.5)',
    detailsBg: '#F7F9FC',
    reportedBg: '#FFF8F0',
    reportedBorder: 'rgba(230,126,34,0.1)',
    closeBg: '#161B22',
    closeBorder: '#30363D',
    closeText: '#E6EDF3',
    deleteBg: 'rgba(214,48,49,0.1)',
    deleteText: '#F85149',
    emptyBg: 'rgba(22,27,34,0.5)',
  } : {
    // Light theme
    bg: '#F7F9FC',
    bgGradient: 'linear-gradient(160deg, #EBF2FA 0%, #F4F7FA 40%, #F7F9FC 100%)',
    card: 'rgba(255,255,255,0.85)',
    cardBorder: 'rgba(59,125,216,0.06)',
    cardShadow: '0 2px 12px rgba(0,0,0,0.02)',
    nav: 'rgba(255,255,255,0.8)',
    navBorder: 'rgba(59,125,216,0.06)',
    text: '#1a1a2e',
    textSecondary: '#8E8E93',
    textMuted: '#C7C7CC',
    input: '#F2F2F7',
    inputBorder: '#E5E5EA',
    button: 'linear-gradient(135deg, #3B7DD8 0%, #2E6BC4 100%)',
    panel: '#FFFFFF',
    panelBorder: '#F2F2F7',
    sectionBg: '#F7F9FC',
    sectionBorder: 'rgba(59,125,216,0.08)',
    chatBubble: '#F2F2F7',
    chatBubbleBorder: 'transparent',
    filterActive: '#fff',
    filterActiveBg: '#1a1a2e',
    filterInactive: '#8E8E93',
    filterInactiveBg: 'rgba(255,255,255,0.6)',
    overlay: 'rgba(0,0,0,0.2)',
    detailsBg: '#F7F9FC',
    reportedBg: '#FFF8F0',
    reportedBorder: 'rgba(230,126,34,0.1)',
    closeBg: '#fff',
    closeBorder: '#E5E5EA',
    closeText: '#1a1a2e',
    deleteBg: '#FFF2F2',
    deleteText: '#D63031',
    emptyBg: 'rgba(255,255,255,0.5)',
  };

  return (
    <ThemeContext.Provider value={{ dark, toggle, t }}>
      {children}
      {/* Floating toggle button */}
      <button
        onClick={toggle}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 48,
          height: 48,
          borderRadius: 16,
          border: 'none',
          background: dark ? '#30363D' : '#FFFFFF',
          boxShadow: dark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.12)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          zIndex: 200,
          transition: 'all 0.2s ease',
        }}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {dark ? '☀️' : '🌙'}
      </button>
    </ThemeContext.Provider>
  );
}